<template>
<div id="timesheet-view">
  <forecast-view-sidebar
    :isSidebarActive="addNewDataSidebar"
    @closeSidebar="toggleDataSidebar"
    :data="sidebarData"
    :locations="locations"
    :projectId="selectedProjectId"
    :initialDate="selectedStartDateTime"
    @onForecastUpdated="forecastUpdated"
    @onForecastInserted="forecastInserted"
  />

  <!-- Tab bar only (feature 19470): the Monthly content stays mounted behind v-show so the existing grid is
       never destroyed by the tab switch; the Annual tab is created on demand. -->
  <vs-tabs v-model="selectedTab" class="forecast-tabs">
    <vs-tab :label="$t('GradeControl.Forecast.MonthlyTab')" icon-pack="feather" icon="icon-calendar" />
    <vs-tab :label="$t('GradeControl.Forecast.AnnualTab')" icon-pack="feather" icon="icon-bar-chart-2" />
  </vs-tabs>
  <div v-show="selectedTab === 0">
  <vx-card ref="listCard" :title="$t('GradeControl.Forecast.ForecastList')"  class="forecast-list-filters mb-3" title-color="black" refresh-content-action @refresh="getForecastEntries" >
    <vs-row vs-type="flex" vs-justify="space-between">
      <vs-col vs-type="flex" vs-justify="flex-start" vs-lg="2" vs-sm="6" vs-xs="12" >
        <div class="vx-row">
          <div class="vx-col w-full" >
            <form-label class="mr-2">{{$t('GradeControl.Forecast.SelectMonth')}}</form-label>
            <flat-pickr :config="configMonthPicker" v-model="selectedMonth" :placeholder="$t('GradeControl.Forecast.SelectMonth')" />
          </div>
        </div>
      </vs-col>
      <vs-col vs-type="flex" vs-justify="flex-start" vs-align="left" vs-lg="4" vs-sm="6" vs-xs="12" v-if="useBucketInput()" >
        <div>
          <form-label class="mr-2">{{ $t('GradeControl.RemuckForecast.Title') }}</form-label>
          <vx-input-group >
            <validated-input
              label=""
              v-model="remuckForecastDensity"
              disabled
            ></validated-input>
            <template slot="append" >
              <div class="bg-light">
                <feather-icon icon="EditIcon" class="cursor-pointer m-2" svg-classes="h-6 w-6" @click="openEditDensityPopup" v-b-tooltip.hover :title="$t('GradeControl.RemuckForecast.Edit')"/>
              </div>
            </template>
          </vx-input-group>
        </div>
      </vs-col>

      <vs-col vs-type="flex" vs-justify="flex-end" vs-align="center" vs-lg="4" vs-sm="12" vs-xs="12" class="mt-3">
        <vx-tooltip :text="$t('AddNew')" position="bottom" delay=".3s">
           <feather-icon icon="PlusCircleIcon" class="cursor-pointer ml-5" svg-classes="h-6 w-6" @click="addNewData" />
        </vx-tooltip>
        <vx-tooltip :text="$t('Approve')" position="bottom" delay=".3s">
          <feather-icon icon="CheckCircleIcon" class="cursor-pointer ml-5" svg-classes="h-6 w-6" @click="openApprobationConfirm" />
        </vx-tooltip>
        <vx-tooltip :text="$t('GradeControl.Forecast.CancelApprobation')" position="bottom" delay=".3s">
          <feather-icon icon="XIcon" class="cursor-pointer ml-5" svg-classes="h-6 w-6" @click="openCancelApprobationConfirm" />
        </vx-tooltip>
        <vx-tooltip :text="$t('GradeControl.Forecast.CopyNextMonth')" position="bottom" delay=".3s">
          <feather-icon icon="LogOutIcon" class="cursor-pointer ml-5" svg-classes="h-6 w-6" @click="openCopyNextMonthConfirm" />
        </vx-tooltip>
        <vx-tooltip :text="$t('Delete')" position="bottom" delay=".3s">
          <feather-icon icon="TrashIcon" class="cursor-pointer ml-5" svg-classes="h-6 w-6" @click="openDeleteConfirm" />
        </vx-tooltip>
         <vx-tooltip :text="$t('Upload')" position="bottom" delay=".3s">
          <feather-icon icon="UploadIcon" class="cursor-pointer ml-5" svg-classes="h-6 w-6" @click="openUploadPopup" />
        </vx-tooltip>
      </vs-col>
    </vs-row>
    <vs-row v-if="featureNsrEnabled">
      <NSRDisplay
          :projectId="selectedProjectId"
          :refDate="selectedStartDateTime"
          class="nsr-display"
        />
    </vs-row>
    <ag-grid-vue
      ref="agGridTable"
      :components="components"
      :frameworkComponents="frameworkComponents"
      :gridOptions="gridOptions"
      :context="context"
      class="ag-theme-material w-100 my-4 ag-grid-table"
      :columnDefs="columnDefs"
      :defaultColDef="defaultColDef"
      :rowData="forecastList"
      rowSelection="multiple"
      :rowClassRules="rowClassRules"
      colResizeDefault="shift"
      :animateRows="true"
      :pagination="false"
      :suppressPaginationPanel="true"
      :enableCellChangeFlash="false"
      :enableRtl="$vs.rtl"></ag-grid-vue>
  </vx-card>
  </div>
  <annual-forecast v-if="selectedTab === 1" :projectId="selectedProjectId" :locations="locations" @locationsChanged="reloadLocations" />
  <vs-popup class="uploadPopup" :title="$t('GradeControl.Forecast.UploadFile')" :active.sync="uploadPopupActive">
    <div class="p-6">
      <form data-vv-scope="tab2">
        <vs-row >
          <vs-col vs-w="12">
            <input id="uploadInputFile" type="file" accept=".csv" v-on:change="getFile($event)">
            <div class="mb-1">
            </div>
            <validation-message v-if="showInvalidFileError">{{ $t('GradeControl.Forecast.UploadInvalidFileError') }}</validation-message>
          </vs-col>
        </vs-row>
         <vs-row class="mb-6" >
          <vs-col vs-w="6">
            <small><a target="_blank" href="https://agnicoeagle.sharepoint.com/:x:/s/GRP_CSD_Data_Solution/Ee-NOPy3dRBKg6JVN_2zK-oBNTYlys8nidVqRf4ifICt6A?e=Yese56&download=1">{{$t('GradeControl.Forecast.TemplateFile')}}</a></small>
          </vs-col>
        </vs-row>
      </form>
      <div>
        <vs-row class="mb-3" vs-type="flex" vs-justify="flex-end">
           <vs-col vs-type="flex" vs-justify="center" vs-align="center" vs-w="12">
            <vs-button :disabled="!file" class="mr-5" @click="uploadFile">{{ $t('Submit') }}</vs-button>
            <vs-button type="border" color="danger" @click="cancelUpload">{{ $t('Cancel') }}</vs-button>
          </vs-col>
        </vs-row>
        <vs-row>
          <vs-col vs-w="12">
             <vs-progress v-show="uploadInProgress" indeterminate color="primary">primary</vs-progress>
          </vs-col>
        </vs-row>
      </div>
    </div>
  </vs-popup>
  <vs-popup class="editDensityPopup" :title="$t('GradeControl.RemuckForecast.Title')" :active.sync="editDensityPopupActive">
    <div class="p-3">
        <vs-row class="mb-3">
          <vs-col vs-w="12">
            <ValidationProvider :name="$t('GradeControl.RemuckForecast.Density')" rules="required|decimal" v-slot="{ errors }">
              <validated-input

                :label="$t('GradeControl.RemuckForecast.Density')"
                v-model="editRemuckForecastDensity"
              ></validated-input>
              <validation-message
                >{{ errors[0] }}</validation-message>
            </ValidationProvider>
          </vs-col>
        </vs-row>
      <div>
        <vs-row class="mb-3" vs-type="flex" vs-justify="flex-end">
           <vs-col vs-type="flex" vs-justify="right" vs-align="right" vs-w="12">
            <vs-button class="mr-5" @click="confirmEditDensity">{{ $t('Submit') }}</vs-button>
            <vs-button type="border" color="danger" @click="cancelEditDensity">{{ $t('Cancel') }}</vs-button>
          </vs-col>
        </vs-row>
      </div>
    </div>
  </vs-popup>
</div>

</template>
<script>
import { AgGridVue } from "ag-grid-vue";
import "@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss";

import Vue from "vue";
import moment from 'moment'
import ForecastViewSidebar from "./components/forecastSideView";
import {
  DateCell,
  ErrorBoundary,
  ValidatedInput,
  ValidationMessage,
  FormLabel
} from "./../../../shared/components";
import Datepicker from 'vuejs-datepicker';
import moduleGradeControl from "@/store/geo/gradecontrol/moduleGradeControl.js";
import ElementEnum from '@/app/shared/ElementEnum.js';
import NSRForecastTypeEnum from './../NSRForecastTypeEnum.js';
import FlatPickr  from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import monthSelectPlugin from 'flatpickr/dist/plugins/monthSelect/index.js'
import 'flatpickr/dist/plugins/monthSelect/style.css'

import ForecastActionsCellRenderer from './components/ForecastActionsCellRenderer.vue'
import ForecastResultCellRenderer from './components/ForecastResultCellRenderer.vue'
import LocationCellRenderer from './../components/LocationCellRenderer.vue'
import LocationIdCellEditor from './components/LocationIdCellEditor.vue'
import ForecastResultCellEditor from './components/ForecastResultCellEditor.vue'

import NSRDisplay from "./components/NSRDisplay.vue";
import AnnualForecast from "./annual/AnnualForecast.vue";

import featureFlagsService from '@/app/shared/services/featureFlagsService.js'
import features from "@/app/shared/AppFeatures.js"

export default {
  /* eslint-disable vue/no-unused-components */
  components: {
    AgGridVue,
    DateCell,
    ErrorBoundary,
    Datepicker,
    ForecastViewSidebar,
    ForecastActionsCellRenderer,
    ForecastResultCellRenderer,
    LocationIdCellEditor,
    ForecastResultCellEditor,
    LocationCellRenderer,
    FlatPickr,
    monthSelectPlugin,
    ValidatedInput,
    ValidationMessage,
    FormLabel,
    NSRDisplay,
    AnnualForecast
  },
  data() {
    return {
      selectedTab: 0,
      selectedMonth: null,
      selectedProjectId: null,
      addNewDataSidebar: false,
      uploadPopupActive: false,
      editDensityPopupActive: false,
      showInvalidFileError: false,
      uploadInProgress: false,
      sidebarData: {},
      remuckForecast: {
        id: 0,
        projectId: null,
        startDate: null,
        endDate: null,
        density: null
      },
      editRemuckForecastDensity: null,
      previousDensityValue: null,
      file: "",
      defaultColDef: {
        sortable: true,
        resizable: true,
        editable: (params) => {
          return params.data.approbationDate === null
        }
      },
      gridApi: null,
      gridOptions: {
        onCellValueChanged: (params) => {
          this.updateForecast(params.data);
        },
        getRowNodeId: (data) => {
          return data.locationForecastId;
        }
      },
      rowClassRules: {
        'forecast-approved': 'data.approbationDate !== null'
      },
      context: { componentParent: this },
      columnDefs: [
        {
          headerName: 'Actions',
          field: 'locationForecastId',
          width: 170,
          editable: false,
          sortable: false,
          checkboxSelection: true,
          headerCheckboxSelectionFilteredOnly: true,
          headerCheckboxSelection: true,
          cellRendererFramework: Vue.extend(ForecastActionsCellRenderer)
        },
        {
          headerName: this.$t('GradeControl.Location.Name'),
          field: "locationId",
          width: 155,
          filter: true,
          filterParams: {
            valueGetter: params => {
              const location = this.context.componentParent.locations.find(l => l.locationId === params.data.locationId)
              if (location) {
                return location.code;
              }
              else {
                return "";
              }
            }
          },
          cellRendererFramework: Vue.extend(LocationCellRenderer),
          cellEditorFramework: Vue.extend(LocationIdCellEditor),
          comparator:  (valueA, valueB) => {
            if (valueA.locationCode === valueB.locationCode) {
              return 0;
            }
            else {
              return (valueA.locationCode > valueB.locationCode) ? 1 : -1;
            }
          },
          valueGetter: (params) => {
            const locationId = params.data.locationId;
            const locations = this.context.componentParent.locations;
            let locationType = null;
            let locationCode = "";

            if (params.data.locationId !== null) {

              const location = params.context.componentParent.locations.find(l => l.locationId === params.data.locationId)

              if (location) {
                locationType = location.type;
                locationCode = location.code;
              }
            }

            return {
              locationId,
              locationType,
              locationCode,
              locations
            };
          }
        },
        {
          headerName: this.$t('GradeControl.Location.AlternateCodesShort'),
          field: "locationId",
          width: 175,
          editable: false,
          sortable: false,
          valueGetter: (params) => {
            if (params.data.locationId !== null) {

              const location = params.context.componentParent.locations.find(l => l.locationId === params.data.locationId)

              if (location) {
                return location.alternateCodes ? location.alternateCodes : "N/A";
              }
            }

            return "N/A";
          }
        },
        {
          headerName: this.$systemUnits.getDensityLabel(),
          field: "density",
          width: 175,
          filter:'agNumberColumnFilter',
          hide: !this.useBucketInput(),
          valueSetter: (params) => {
            if (isNaN(params.newValue))
              return false;

            const count = parseInt(params.newValue, 10);
            if (count < 0)
              return false;

            params.data.density = this.$systemUnits.convertDensityToMetric(params.newValue);
            return true;
          },
          valueGetter: (params) => {

            return this.$systemUnits.convertDensityRounded(params.data.density);
          }
        },
        {
          headerName: this.$systemUnits.getTonnageLabel(),
          field: "tonnage",
          width: 175,
          filter:'agNumberColumnFilter',
          valueSetter: (params) => {
            if (isNaN(params.newValue))
              return false;

            const tonnage = parseFloat(params.newValue);
            if (tonnage < 0)
              return false;

            params.data.tonnage = this.$systemUnits.convertTonnageToMetric(params.newValue);
            return true;
          },
          valueGetter: (params) => {
            const value = this.$systemUnits.convertTonnage(params.data.tonnage);
            return value % 1 === 0 || value.toFixed(2) % 1 === 0 ? value.toFixed(0) : value.toFixed(2);
          }
        },
        {
          headerName: this.$systemUnits.getGradeLabel('Gold'),
          field: "results",
          width: 150,
          cellRendererFramework: Vue.extend(ForecastResultCellRenderer),
          cellEditorFramework: Vue.extend(ForecastResultCellEditor),
          filter:'agNumberColumnFilter',
          filterParams: {
            valueGetter: params => {
              return this.getGrade(params, ElementEnum.Gold);
            }
          },
          valueGetter: (params) => {
            return {
              grade : this.getGrade(params, ElementEnum.Gold),
              cutOff : this.getCutoff(params, ElementEnum.Gold),
              element : ElementEnum.Gold
            }
          },
          valueSetter: (params) => {
            return this.setForecastResult(params, ElementEnum.Gold);
          },
          comparator:  (valueA, valueB) => {
            return this.compareGrade(valueA, valueB);
          }
        },
        {
          headerName: this.$systemUnits.getGradeLabel('Silver'),
          field: "results",
          width: 150,
          cellRendererFramework: Vue.extend(ForecastResultCellRenderer),
          cellEditorFramework: Vue.extend(ForecastResultCellEditor),
          filter:'agNumberColumnFilter',
          filterParams: {
            valueGetter: params => {
              return this.getGrade(params, ElementEnum.Silver);
            }
          },
          valueGetter: (params) => {
            return {
              grade : this.getGrade(params, ElementEnum.Silver),
              cutOff : this.getCutoff(params, ElementEnum.Silver),
              element : ElementEnum.Silver
            }
          },
          valueSetter: (params) => {
            return this.setForecastResult(params, ElementEnum.Silver);
          },
          comparator:  (valueA, valueB) => {
            return this.compareGrade(valueA, valueB);
          }
        },
        {
          headerName: this.$t('Copper') + " (%)",
          field: "results",
          width: 150,
          cellRendererFramework: Vue.extend(ForecastResultCellRenderer),
          cellEditorFramework: Vue.extend(ForecastResultCellEditor),
          filter:'agNumberColumnFilter',
          filterParams: {
            valueGetter: params => {
              return this.getGrade(params, ElementEnum.Copper);
            }
          },
          valueGetter: (params) => {
            return {
              grade : this.getGrade(params, ElementEnum.Copper),
              cutOff : this.getCutoff(params, ElementEnum.Copper),
              element : ElementEnum.Copper
            }
          },
          valueSetter: (params) => {
            return this.setForecastResult(params, ElementEnum.Copper);
          },
          comparator:  (valueA, valueB) => {
            return this.compareGrade(valueA, valueB);
          }
        },
        {
          headerName: this.$t('Zinc') + " (%)",
          field: "results",
          width: 150,
          cellRendererFramework: Vue.extend(ForecastResultCellRenderer),
          cellEditorFramework: Vue.extend(ForecastResultCellEditor),
          filter:'agNumberColumnFilter',
          filterParams: {
            valueGetter: params => {
              return this.getGrade(params, ElementEnum.Zinc);
            }
          },
          valueGetter: (params) => {
            return {
              grade : this.getGrade(params, ElementEnum.Zinc),
              cutOff : this.getCutoff(params, ElementEnum.Zinc),
              element : ElementEnum.Zinc
            }
          },
          valueSetter: (params) => {
            return this.setForecastResult(params, ElementEnum.Zinc);
          },
          comparator:  (valueA, valueB) => {
            return this.compareGrade(valueA, valueB);
          }
        },
        {
          headerName: this.$t('Lead') + " (%)",
          field: "results",
          width: 150,
          cellRendererFramework: Vue.extend(ForecastResultCellRenderer),
          cellEditorFramework: Vue.extend(ForecastResultCellEditor),
          filter:'agNumberColumnFilter',
          filterParams: {
            valueGetter: params => {
              return this.getGrade(params, ElementEnum.Lead);
            }
          },
          valueGetter: (params) => {
            return {
              grade : this.getGrade(params, ElementEnum.Lead),
              cutOff : this.getCutoff(params, ElementEnum.Lead),
              element : ElementEnum.Lead
            }
          },
          valueSetter: (params) => {
            return this.setForecastResult(params, ElementEnum.Lead);
          },
          comparator:  (valueA, valueB) => {
            return this.compareGrade(valueA, valueB);
          }
        },
        {
          headerName: this.$t('Carbon') + " (%)",
          field: "results",
          width: 150,
          cellRendererFramework: Vue.extend(ForecastResultCellRenderer),
          cellEditorFramework: Vue.extend(ForecastResultCellEditor),
          filter:'agNumberColumnFilter',
          filterParams: {
            valueGetter: params => {
              return this.getGrade(params, ElementEnum.Carbon);
            }
          },
          valueGetter: (params) => {
            return {
              grade : this.getGrade(params, ElementEnum.Carbon),
              cutOff : this.getCutoff(params, ElementEnum.Carbon),
              element : ElementEnum.Carbon
            }
          },
          valueSetter: (params) => {
            return this.setForecastResult(params, ElementEnum.Carbon);
          },
          comparator:  (valueA, valueB) => {
            return this.compareGrade(valueA, valueB);
          }
        },
        {
          headerName: this.$t('Sulfur') + " (%)",
          field: "results",
          width: 150,
          cellRendererFramework: Vue.extend(ForecastResultCellRenderer),
          cellEditorFramework: Vue.extend(ForecastResultCellEditor),
          filter:'agNumberColumnFilter',
          filterParams: {
            valueGetter: params => {
              return this.getGrade(params, ElementEnum.Sulfur);
            }
          },
          valueGetter: (params) => {
            return {
              grade : this.getGrade(params, ElementEnum.Sulfur),
              cutOff : this.getCutoff(params, ElementEnum.Sulfur),
              element : ElementEnum.Sulfur
            }
          },
          valueSetter: (params) => {
            return this.setForecastResult(params, ElementEnum.Sulfur);
          },
          comparator:  (valueA, valueB) => {
            return this.compareGrade(valueA, valueB);
          }
        },
        {
          headerName: this.$t('GradeControl.NSRForecast.NSR3Months'),
          field: "nsrMonthly",
          width: 150,
          valueFormatter: (params) => {
            return this.$options.filters.toCurrencyDecimal(params.value);
          }
        },
        {
          headerName: this.$t('GradeControl.NSRForecast.NSRReserve'),
          field: "nsrReserve",
          width: 150,
          valueFormatter: (params) => {
            return this.$options.filters.toCurrencyDecimal(params.value);
          }
        }
      ],
      components: {
        ForecastActionsCellRenderer,
        ForecastResultCellRenderer,
        LocationCellRenderer
      },
      frameworkComponents: {
        LocationIdCellEditor,
        ForecastResultCellEditor
      },
      configMonthPicker: {
        // enableTime: false,
        // dateFormat: 'YY-mm-dd',
        allowInput: false,
        monthSelectorType: "static",
        plugins: [
          new monthSelectPlugin({
            shorthand: true, //defaults to false
            dateFormat: "Y-m", //defaults to "F Y"
            altFormat: "F Y" //defaults to "F Y"
          })
        ]
      }
    };
  },

  computed: {
    locations() {
      return this.$store.state.gradeControl.locations;
    },
    forecastList() {
      return this.$store.state.gradeControl.forecasts;
    },
    selectedStartDateTime() {
      if (this.selectedMonth) {
        return new Date(this.selectedMonth + "-01 05:00");
      }
      return new Date();
    },
    remuckForecastList() {
      return this.$store.state.gradeControl.remuckForecasts;
    },
    endDate() {
      const previous = new Date(this.selectedStartDateTime);
      const nextMonth = moment(previous).add(1, 'month').add(-1, 'days');
      return nextMonth.toDate();
    },
    nsrForecastTypes() {
      return NSRForecastTypeEnum.definition;
    },
    featureNsrEnabled() {
      return featureFlagsService.isEnabled(features.Core.ProjectFilter.Nsr)
    },
    remuckForecastDensity() {
      if (this.remuckForecast && this.remuckForecast.density !== null) {
        return this.$systemUnits.convertDensityRounded(Number(this.remuckForecast.density));
      }
      else {
        return null;
      }
    }
  },
  watch: {
    selectedMonth(val) {
      if (!val) return;
      const remuckForecast = this.remuckForecastList.find(o => o.startDate.slice(0, 10) === this.selectedStartDateTime.toISOString().slice(0, 10)
        && o.endDate.slice(0, 10) === this.endDate.toISOString().slice(0, 10));
      if (remuckForecast !== undefined && remuckForecast !== null) {
        this.remuckForecast = remuckForecast;
      }
      else {
        this.remuckForecast = {
          id: 0,
          projectId:  this.selectedProjectId,
          startDate: this.selectedStartDateTime.toISOString().slice(0, 10),
          endDate: this.endDate.toISOString().slice(0, 10),
          density: null
        };
      }
      this.$refs.listCard.refreshcard();
    }
  },
  mounted() {
    this.gridApi = this.gridOptions.api;

    this.$store.dispatch("gradeControl/clearForecastListItems");
  },
  created() {
    if (!moduleGradeControl.isRegistered) {
      this.$store.registerModule("gradeControl", moduleGradeControl);
      moduleGradeControl.isRegistered = true;
    }

    this.selectedProjectId = Number(this.$store.state.projectId);

    this.$vs.loading();
    Promise.all([
      this.$store.dispatch("gradeControl/fetchLocationListItems", this.selectedProjectId),
      this.$store.dispatch("gradeControl/fetchRemuckForecastListItems", this.selectedProjectId)
    ]).then(() => {
      const today = new Date();
      this.selectedMonth = today.getFullYear() + "-" + (today.getMonth() + 1);
    }).finally(() => {
      this.$vs.loading.close();
    })
  },
  methods: {
    openUploadPopup() {
      this.uploadPopupActive = true;
    },
    getFile(event) {
      if (event.target.files.length > 0) {
        if (event.target.files[0].name !== null && event.target.files[0].name !== "undefined") {
          if (event.target.files[0].name.search(".csv") !== -1) {
            this.file = event.target.files[0];
            this.showInvalidFileError = false;
          }
          else {
            this.showInvalidFileError = true;
          }
        }
      }
    },
    uploadFile() {
      const formData = new FormData();
      formData.append("files", this.file);
      formData.append("projectId", this.selectedProjectId);
      formData.append("startDate", this.$options.filters.toDate(this.selectedStartDateTime));

      const config = {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      };

      this.uploadInProgress = true;
      this.$webApi.post('geo/gradecontrol/locationforecast/upload/', formData, config)
        .then((response) => {
          if (response.status === 200) {
            this.uploadInProgress  = false;
            this.uploadPopupActive = false;
            this.$vs.notify({
              color: "success",
              title: this.$t("Success"),
              text: this.$t("GradeControl.Forecast.UploadSuccess")
            });

            // refresh location lists (as new location could have been created from the template)
            this.$vs.loading();
            this.$store.dispatch("gradeControl/fetchLocationListItems", this.selectedProjectId).then(() => {
              this.$refs.listCard.refreshcard()
            }).finally(() => {
              this.$vs.loading.close();
            });
          }
        }).catch((error) => {
          this.uploadInProgress  = false;
          this.uploadPopupActive = false;

          if (error.response.status === 400) {
            this.$vs.notify({
              color: "danger",
              title: this.$t("GradeControl.Forecast.UploadFails"),
              text:  this.$i18n.locale.startsWith('fr') ? error.response.data.frMessage : error.response.data.enMessage});
          } else {
            this.$vs.notify({
              color: "danger",
              title: this.$t("GradeControl.Forecast.UploadFails"),
              text: this.$t("GradeControl.Forecast.UploadFailText")
            });
          }

        }).finally(() => {
          document.getElementById("uploadInputFile").value = "";
          this.file = null;
        })
    },
    cancelUpload() {
      document.getElementById("uploadInputFile").value = "";
      this.file = null;
      this.uploadPopupActive = false;
    },

    openEditDensityPopup() {
      this.editRemuckForecastDensity = this.$systemUnits.convertDensity(this.remuckForecast.density);
      this.previousDensityValue = this.remuckForecast.density;
      this.editDensityPopupActive = true;
    },
    cancelEditDensity() {
      this.editDensityPopupActive = false;
      this.remuckForecast.density = this.previousDensityValue;
    },
    confirmEditDensity() {
      this.editDensityPopupActive = false;
      this.$vs.loading();
      this.remuckForecast.density = this.$systemUnits.convertDensityToMetric(this.editRemuckForecastDensity);
      if (this.remuckForecast.id === 0) {
        this.$store.dispatch("gradeControl/addRemuckForecast", this.remuckForecast).then((response) => {
          this.remuckForecast = response.data.model;
        }).finally(() => {
          this.$vs.loading.close();
        });
      }
      else {
        this.$store.dispatch("gradeControl/updateRemuckForecast", this.remuckForecast).then((response) => {
          this.remuckForecast = response.data.model;
        }).finally(() => {
          this.$vs.loading.close();
        });
      }
    },

    getForecastEntries() {
      if (this.selectedStartDateTime) {
        this.$store.dispatch("gradeControl/fetchLocationForecastListItems",  {
          startDate: this.selectedStartDateTime,
          projectId: this.selectedProjectId
        }).then(() => {
          this.$refs.listCard.removeRefreshAnimation();
        });
      }
      else {
        this.$refs.listCard.removeRefreshAnimation();
      }
    },

    formatDateTime(params) {
      return this.$options.filters.toDate(params.value);
    },
    reloadLocations() {
      this.$store.dispatch("gradeControl/fetchLocationListItems", this.selectedProjectId);
    },
    toggleDataSidebar(val = false) {
      this.addNewDataSidebar = val;
    },
    addNewData() {
      this.sidebarData = {};
      this.toggleDataSidebar(true);
    },
    deleteData(id) {
      this.$store.dispatch("gradeControl/removeForecast", id)
        .then(() => {
          this.$vs.notify({
            color: "success",
            title: this.$t("Success"),
            text: this.$t("GradeControl.Forecast.ForecastDeleted")
          });
        });
    },
    updateForecast(forecast) {
      if (forecast) {
        this.$vs.loading();
        this.$store.dispatch("gradeControl/updateForecast", forecast).then((response) => {
          const rowNode = this.gridApi.getRowNode(response.data.locationForecastId);
          if (rowNode) {
            rowNode.setData(response.data);
          }
        }).finally(() => {
          this.$vs.loading.close();
        });
      }
    },
    forecastUpdated(forecast) {
      this.$vs.notify({
        title: this.$t("Success"),
        text: this.$t("SaveSuccess"),
        color: "success",
        iconPack: 'feather',
        icon:'icon-check'
      });

      this.toggleDataSidebar(false);

      const rowNode = this.gridApi.getRowNode(forecast.locationForecastId);
      if (rowNode) {
        rowNode.setData(forecast);
      }

      this.gridApi.applyTransaction({ update: [forecast] });

    },

    forecastInserted(forecast) {
      this.$vs.notify({
        title: this.$t("Success"),
        text: this.$t("InsertSuccess"),
        color: "success",
        iconPack: 'feather',
        icon:'icon-check'
      });
      this.toggleDataSidebar(false);
      this.gridApi.applyTransaction({ update: [forecast] });
    },

    editData(data) {
      this.sidebarData = data;
      this.toggleDataSidebar(true);
    },

    getGrade(params, elementId) {
      if (params.data.results !== null) {
        const element = params.data.results.find(r => r.element === elementId)
        if (element) {
          return element.grade;
        }
        else {
          return null;
        }
      }
      else {
        return null;
      }
    },
    getCutoff(params, elementId) {
      if (params.data.results !== null) {
        const element = params.data.results.find(r => r.element === elementId)
        if (element) {
          return element.cutOff;
        }
        else {
          return null;
        }
      }
      else {
        return null;
      }
    },
    getMonthly(params) {
      return params.data.results.nsr3Months;
    },
    getReserve(params) {
      return params.data.results.nsrReserve;
    },
    compareGrade(valueA, valueB) {
      if (valueA.grade === valueB.grade) {
        return 0;
      }
      else {
        return (valueA.grade > valueB.grade) ? 1 : -1;
      }
    },
    setForecastResult(params, elementId) {

      const element = params.data.results.find(r => r.element === elementId)
      if (params.newValue.grade !== null && params.newValue.grade !== '')  {
        if (isNaN(params.newValue.grade))
          return false;

        if (params.newValue.cutOff !== '' && isNaN(params.newValue.cutOff))
          return false;

        if (typeof element !== 'undefined') {
          element.grade = this.$systemUnits.convertGradeToMetric(params.newValue.grade, element);
          element.cutOff = this.$systemUnits.convertGradeToMetric(params.newValue.cutOff, element);
        }
        else {
          params.data.results.push({
            locationForecastId : params.data.locationForecastId,
            element : elementId,
            grade : params.newValue.grade,
            cutOff : params.newValue.cutOff
          });
        }

        return true;
      }
      else {
        const index = params.data.results.map(r => r.element).indexOf(elementId);
        params.data.results.splice(index);
        return true;
      }
    },
    openApprobationConfirm() {
      const rows = this.gridApi.getSelectedRows();
      if (rows.length === 0) {
        this.$vs.dialog({
          color: 'danger',
          title: this.$t("Approve"),
          text: this.$t('GradeControl.Forecast.NoForecastSelected')
        })
      }
      else {
        this.$vs.dialog({
          type: "confirm",
          color: "warning",
          title: this.$t("Approve"),
          text: this.$t('GradeControl.Forecast.ConfirmApprobation'),
          accept: this.approve
        });
      }
    },
    openCancelApprobationConfirm() {
      const rows = this.gridApi.getSelectedRows();
      if (rows.length === 0) {
        this.$vs.dialog({
          color: 'danger',
          title: this.$t("GradeControl.Forecast.CancelApprobation"),
          text: this.$t('GradeControl.Forecast.NoForecastSelected')
        })
      }
      else {
        this.$vs.dialog({
          type: "confirm",
          color: "warning",
          title: this.$t("GradeControl.Forecast.CancelApprobation"),
          text: this.$t('GradeControl.Forecast.ConfirmCancelBatchApprobation'),
          accept: this.cancelBatchApprobation
        });
      }
    },

    openCopyNextMonthConfirm() {
      const rows = this.gridApi.getSelectedRows();
      if (rows.length === 0) {
        this.$vs.dialog({
          color: 'danger',
          title: this.$t("GradeControl.Forecast.CopyNextMonth"),
          text: this.$t('GradeControl.Forecast.NoForecastSelected')
        })
      }
      else {
        this.$vs.dialog({
          type: "confirm",
          color: "warning",
          title: this.$t("GradeControl.Forecast.CopyNextMonth"),
          text: this.$t('GradeControl.Forecast.ConfirmCopyNextMonth'),
          accept: this.copyNextMonth
        });
      }
    },

    approve() {
      const rows = this.gridApi.getSelectedRows();
      this.$vs.loading();
      this.$webApi.post('geo/gradecontrol/LocationForecast/Approve/', {
        locationForecastsId: rows.map(l => l.locationForecastId),
        projectId: this.selectedProjectId
      }).then((result) => {

        if (result.data) {
          this.$vs.notify({
            title: this.$t("Import"),
            text: this.$t("GradeControl.Forecast.ApprobationCompleted"),
            color: "success",
            iconPack: 'feather',
            icon:'icon-check'
          });
        }

        this.$refs.listCard.refreshcard();

      }).finally(() => {
        this.$vs.loading.close();
      });
    },

    cancelBatchApprobation() {
      const rows = this.gridApi.getSelectedRows();
      this.$vs.loading();
      this.$webApi.post('geo/gradecontrol/LocationForecast/CancelBatch/', {
        locationForecastsId: rows.map(l => l.locationForecastId),
        projectId: this.selectedProjectId
      }).then((result) => {

        if (result.data) {
          this.$vs.notify({
            title: this.$t("Import"),
            text: this.$t("GradeControl.Forecast.CancellationCompleted"),
            color: "success",
            iconPack: 'feather',
            icon:'icon-check'
          });
        }

        this.$refs.listCard.refreshcard();

      }).finally(() => {
        this.$vs.loading.close();
      });
    },

    copyNextMonth() {
      const rows = this.gridApi.getSelectedRows();
      const d = new Date(this.selectedStartDateTime)
      d.setMonth(d.getMonth() + 1);
      this.$vs.loading();

      this.$webApi.post('geo/gradecontrol/LocationForecast/CopyNextMonth/', {
        locationForecastsId: rows.map(l => l.locationForecastId),
        startDate: d
      }).then((result) => {

        if (result.data) {
          this.$vs.notify({
            title: this.$t("GradeControl.Forecast.CopyNextMonth"),
            text: this.$t("GradeControl.Forecast.CopyNextMonthCompleted"),
            color: "success",
            iconPack: 'feather',
            icon:'icon-check'
          });
        }

        this.$refs.listCard.refreshcard();

      }).finally(() => {
        this.$vs.loading.close();
      });
    },

    duplicateData(forecast) {
      const clonedForecast = Object.assign({}, forecast);

      // reset keys and attributes
      clonedForecast.locationForecastId = 0;
      clonedForecast.ApprovedBy = null;
      clonedForecast.ApprobationDate = null;
      clonedForecast.results.forEach(result => {
        result.forecastResultId = 0;
      });

      this.sidebarData = clonedForecast;
      this.toggleDataSidebar(true);
    },
    cancelApprobation(forecast) {
      forecast.ApprovedBy = null;
      forecast.ApprobationDate = null;
      this.updateForecast(forecast);
    },
    openDeleteConfirm() {
      const rows = this.gridApi.getSelectedRows();
      if (rows.length === 0) {
        this.$vs.dialog({
          color: 'danger',
          title: this.$t("Delete"),
          text: this.$t('GradeControl.Forecast.NoForecastSelected')
        })
      }
      else {
        this.$vs.dialog({
          type: "confirm",
          color: "warning",
          title: this.$t("Delete"),
          text: this.$t('GradeControl.Forecast.ConfirmDeleteMultiple'),
          accept: this.deleteMany
        });
      }
    },
    deleteMany() {
      const rows = this.gridApi.getSelectedRows();
      this.$vs.loading();


      this.$store.dispatch("gradeControl/removeForecastBatch", rows.map(l => l.locationForecastId))
        .then(() => {
          this.$vs.notify({
            color: "success",
            title: this.$t("Success"),
            text: this.$t("GradeControl.Forecast.ForecastDeleted")
          });

        }).finally(() => {
          this.$vs.loading.close();
        });
    },
    useBucketInput() {
      return this.$store.getters.isProjectUsingBucketInput;
    }
  }
};
</script>
<style lang="scss">
#forecast-view {
  .forecast-list-filters {
    .vs__actions {
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-58%);
    }
  }
}

.forecast-approved {
  border-width: 1 !important;
  border-style: solid !important;
  border-color:  #339900 !important;
}

.vs-select--label {
  display: block !important;
  overflow-x: hidden;
  white-space: nowrap;
}

.nsr-display {
  width: 100%;
}

// The tabs carry no content of their own (see the template); hide their empty content pane.
.forecast-tabs .con-tab {
  display: none;
}

</style>
